import PropTypes from "prop-types";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { quotationFlightShape } from "app/utils/propTypes.js";
import Itinerary from "app/pages/.shared/Itinerary.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import Badge from "app/pages/.shared/Badge.jsx";
import AlertMessage from "app/pages/.shared/AlertMessage.jsx";
import {
	FLIGHT_OPTIONS_STATUS,
	FLIGHTS_TAG,
	TRANSPORT_COMPANY_CODES,
	TRANSPORTATION_TYPES,
} from "app/constants";
import get from "lodash/get";
import { connect } from "react-redux";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import { checkSpecialOfferForSelectedDepartureDate } from "app/pages/Booking/Payment/paymentSelector";
import { getSpecialOffer } from "app/pages/FicheProduit/ficheProduitSelector";
import {
	hasQuoteSpecialOffer,
	getMainAirlineName,
} from "app/pages/Booking/Quotation/quotationSelectors";
import LuggageSummary from "app/pages/Booking/Quotation/Flight/LuggageSummary/LuggageSummary";
import "./QuotationFlights.scss";
import FlightWarnings from "app/pages/Booking/Quotation/Flight/FlightWarnings";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import {
	getFlightOptionsList,
	getFlightOptionsOfSelectedFlight,
} from "app/pages/Booking/bookingSelectors";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import { getSupplierClass, normalizeSupplierClassLabel } from "app/utils/utils";

const getTagLabelKeyToDisplay = (flightTag, isTravelWithoutFlight, isEurostar = false) => {
	const TAGS = {
		[FLIGHTS_TAG.CHEAPEST]: isTravelWithoutFlight
			? "quotation.transport.tag.cheapest"
			: "quotation.flight.tag.cheapest",
		[FLIGHTS_TAG.RECOMMENDED]: isTravelWithoutFlight
			? "quotation.transport.tag.recommended"
			: "quotation.flight.tag.recommended",
		[FLIGHTS_TAG.CHEAPEST_TRAIN]: isEurostar
			? "quotation.flight.tag.eurostar"
			: "quotation.flight.tag.cheapest.train",
		[FLIGHTS_TAG.DIRECT_FLIGHT]: "quotation.flight.tag.direct.flight",
		[FLIGHTS_TAG.BAGGAGE_INCLUDED]: "quotation.flight.tag.luggage.included",
		[FLIGHTS_TAG.CHEAPEST_PRIMARY_AIRPORT]: "quotation.flight.tag.cheapest.primary",
	};

	return TAGS?.[flightTag] || null;
};

export const QuotationFlightsItemComponent = ({
	flight = {},
	partnerCode,
	transportationType,
	luggageMaxCount,
	specialOffer = {},
	hasSpecialOffer,
	onChange,
	onLuggageClick,
	hasQuoteSpecialOffer,
	isTransportAvailableForSpecialOffer,
	flightOptionsStatus,
	flightOptionsList = {},
	booking = {},
	displayRouteDetails = false,
	checkedBaggageMaxWeight,
	mainAirlineName,
}) => {
	const flightCode = flight?.code;

	const additionalLuggageCount = booking?.luggage?.quantity || 0;

	const supplierClassPerSegment = normalizeSupplierClassLabel(
		flightOptionsList?.[flightCode]?.flightBagageOptions?.[0]?.supplierClassPerSegment?.[0]
	);

	const shouldShowSupplierClassPerSegment =
		additionalLuggageCount > 0 && supplierClassPerSegment !== undefined;

	const outboundSupplierClass = shouldShowSupplierClassPerSegment
		? supplierClassPerSegment
		: getSupplierClass(flight?.outbound?.legs);
	const inboundSupplierClass = shouldShowSupplierClassPerSegment
		? supplierClassPerSegment
		: getSupplierClass(flight?.inbound?.legs);

	const handleLuggageClick = useCallback(() => {
		if (typeof onLuggageClick === "function") {
			onLuggageClick();
		}
	}, []);

	const handleFlightSelect = useCallback(() => {
		if (typeof onChange === "function") {
			onChange(flight);
		}
	}, [flight.code]);

	const isTravelWithoutFlight = transportationType !== TRANSPORTATION_TYPES.FLIGHT;

	const isEurostar =
		flight?.inbound?.airline?.code === TRANSPORT_COMPANY_CODES.EUROSTAR &&
		flight?.outbound?.airline?.code === TRANSPORT_COMPANY_CODES.EUROSTAR;

	const tagLabelKey = getTagLabelKeyToDisplay(flight.tag, isTravelWithoutFlight, isEurostar);

	const hasMainAirline = Boolean(mainAirlineName);
	const cheapestAirlineName = flight?.outbound?.airline?.name;
	const isFlightCheapest = flight.tag === FLIGHTS_TAG.CHEAPEST;
	const shouldShowAirlineTag =
		hasMainAirline && (isFlightCheapest || flight.tag === FLIGHTS_TAG.MAIN_AIRLINE);
	const shouldShowDisclaimer = hasMainAirline && isFlightCheapest;
	const shouldHideAirlineNote = hasMainAirline && isFlightCheapest;

	const hasOperatingAirlineWarning =
		(flight?.outbound?.legs?.some(leg => leg.operatingAirlineName) ||
			flight?.inbound?.legs?.some(leg => leg.operatingAirlineName)) &&
		partnerCode === "AFEN";

	const badgeNode = flight.included ? (
		<Badge>
			<FormattedMessage id="general.included" />
		</Badge>
	) : (
		<div className="quotation-flights__upgrade-price">
			<AmountContainer
				prefix={flight.upgradePrice < 0 ? "" : <AmountSign sign="+" />}
				amount={flight.upgradePrice || 0}
			/>
		</div>
	);

	const isRyanair =
		get(flight, "inbound.airline.code") === TRANSPORT_COMPANY_CODES.RYANAIR ||
		get(flight, "outbound.airline.code") === TRANSPORT_COMPANY_CODES.RYANAIR;

	const outBoundDestinationIataCityCode =
		flight?.outbound?.legs &&
		flight?.outbound?.legs[flight?.outbound?.legs?.length - 1]?.to?.iataCity;

	const flightOutboundAirlineCode = flight?.outbound?.airline?.code;

	const isDirectFlight = flight?.outbound?.legs?.length === 1;

	// TODO a supprimer quand la vente tahiti 2023 sera fini.
	const isTahitiWithTechnicalStop =
		(flightOutboundAirlineCode === TRANSPORT_COMPANY_CODES.AIR_FRANCE ||
			flightOutboundAirlineCode === TRANSPORT_COMPANY_CODES.AIR_TAHITI_NUI ||
			flightOutboundAirlineCode === TRANSPORT_COMPANY_CODES.AIR_CARAIBES ||
			flightOutboundAirlineCode === TRANSPORT_COMPANY_CODES.FRENCH_BEE) &&
		isDirectFlight &&
		(outBoundDestinationIataCityCode === "PPT" ||
			outBoundDestinationIataCityCode === "BOB" ||
			outBoundDestinationIataCityCode === "MOZ" ||
			outBoundDestinationIataCityCode === "MOZ" ||
			outBoundDestinationIataCityCode === "RGI");

	// should not display the message if there is already one on top the page (when hasQuoteSpecialOffer = false)
	const showWarningFlightNotAvailableForSpecialOffer =
		hasSpecialOffer && !isTransportAvailableForSpecialOffer && hasQuoteSpecialOffer;

	const flightOptions = flightOptionsList[(flight?.code)];

	return (
		<RadioFormik
			id={flight.code}
			showIcon={true}
			name="flight"
			toggle={handleFlightSelect}
			value={flight.code}
			data-testid={flight.tag || flight.code}
			body={
				<>
					{shouldShowDisclaimer && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							className="quotation-flights__lowcost-disclaimer"
							data-testid="flight-lowcost-disclaimer"
						>
							<FormattedMessage
								id="quotation.flight.cheapest.lowcost.disclaimer"
								values={{
									lowCostAirline: cheapestAirlineName,
									mainAirline: mainAirlineName,
								}}
							/>
						</Typography>
					)}
					<div className="quotation-flights__itinerary">
						<div className="quotation-flights__itinerary-overview">
							<Itinerary
								transportationType={transportationType}
								luggageMaxCount={luggageMaxCount}
								onLuggageClick={handleLuggageClick}
								inbound={flight.inbound}
								outbound={flight.outbound}
								partnerCode={partnerCode}
								displayRouteDetails={displayRouteDetails}
								hideAirlineNote={shouldHideAirlineNote}
								outboundSupplierClass={outboundSupplierClass}
								inboundSupplierClass={inboundSupplierClass}
							/>

							<LuggageSummary
								luggageMaxCount={luggageMaxCount}
								flight={flight}
								key={flightOptionsStatus} // pansement propre qui cache une hémorragie interne (voir quotationReducer case: FETCH_FLIGHT_OPTIONS_SUCCESS)
								transportationType={transportationType}
								onLuggageClick={handleLuggageClick}
								flightOptionsStatus={flightOptionsStatus}
								flightOptions={flightOptions}
								bookingLuggageCount={booking?.luggage?.quantity}
								bookingCabinLuggage={booking?.cabinLuggage}
								maxWeightPerCheckedBaggage={
									booking?.luggage?.maxWeightPerBaggage || checkedBaggageMaxWeight
								}
							/>

							{isTahitiWithTechnicalStop && (
								<div className="quotation-flights__itinerary-warnings">
									<AlertMessage
										message={
											<div className="warnings">
												Ce vol transite par les États-Unis pour une escale
												technique. Vous devez solliciter une demande d'ESTA
												au moins 72 heures avant le départ.
											</div>
										}
									/>
								</div>
							)}

							{isRyanair && (
								<div
									className="quotation-flights__itinerary-warnings quotation-flights__itinerary-warnings--ryanair"
									data-testid="flight-warnings-ryanair"
								>
									<AlertMessage
										message={
											<div className="warnings">
												<FormattedMessage
													id="quotation.flight.ryanair.luggage.warning"
													values={{
														// eslint-disable-next-line react/no-multi-comp
														div: chunks => <div>{chunks}</div>,
													}}
												/>
											</div>
										}
									/>
								</div>
							)}

							{showWarningFlightNotAvailableForSpecialOffer && (
								<div
									className="quotation-flights__itinerary-warnings"
									data-testid="flight-warnings-special-offer"
								>
									<AlertMessage
										message={
											<div className="warnings">
												Ce vol n'est pas éligible à l'opération{" "}
												{specialOffer.title}
											</div>
										}
									/>
								</div>
							)}

							<FlightWarnings
								warnings={flight.warnings}
								flightType={flight.type}
								flightData={flight}
							/>

							{hasOperatingAirlineWarning && (
								<div
									className="quotation-flights__itinerary-warnings quotation-flights__itinerary-warnings--operating"
									data-testid="flight-warnings-operating"
								>
									<AlertMessage
										message={
											<FormattedMessage id="quotation.flight.flight.operator.warning.label" />
										}
									/>
								</div>
							)}
						</div>
					</div>
				</>
			}
		>
			<div className="quotation-flights__content">
				<Typography
					variant={TYPOGRAPHY_VARIANTS.LARGE}
					isBold
					className="quotation-flights__label"
					data-testid="flight-tag"
					component="p"
				>
					{shouldShowAirlineTag ? (
						<FormattedMessage
							id="quotation.flight.tag.cheapest.with.airline"
							values={{ airline: cheapestAirlineName }}
						/>
					) : (
						tagLabelKey && <FormattedMessage id={tagLabelKey} />
					)}
				</Typography>

				<div className="quotation-flights__included" data-testid="flight-upgrade-price">
					{badgeNode}
				</div>
			</div>
		</RadioFormik>
	);
};

QuotationFlightsItemComponent.propTypes = {
	transportationType: PropTypes.oneOf([TRANSPORTATION_TYPES.TRAIN, TRANSPORTATION_TYPES.FLIGHT]),
	onLuggageClick: PropTypes.func,
	onChange: PropTypes.func,
	flight: quotationFlightShape,
	partnerCode: PropTypes.string,
	luggageMaxCount: PropTypes.number,
	specialOffer: PropTypes.object,
	flightOptionsList: PropTypes.object,
	hasSpecialOffer: PropTypes.bool,
	hasQuoteSpecialOffer: PropTypes.bool,
	isTransportAvailableForSpecialOffer: PropTypes.bool,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
	booking: PropTypes.object,
	displayRouteDetails: PropTypes.bool,
	checkedBaggageMaxWeight: PropTypes.number,
	mainAirlineName: PropTypes.string,
};

const mapStateToProps = state => ({
	partnerCode: state.partner.code,
	specialOffer: getSpecialOffer(state),
	hasSpecialOffer: checkSpecialOfferForSelectedDepartureDate(state),
	hasQuoteSpecialOffer: hasQuoteSpecialOffer(state),
	flightOptionsStatus: state.quotation.flightOptionsStatus,
	departureDate: state.booking?.departureDate,
	flightOptionsList: getFlightOptionsList(state),
	booking: state.booking,
	checkedBaggageMaxWeight: getFlightOptionsOfSelectedFlight(state)?.flightBagageOption
		?.maxWeightPerBaggage,
	mainAirlineName: getMainAirlineName(state),
});

export default connect(mapStateToProps)(QuotationFlightsItemComponent);
